import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { GetAllPlans, Plan } from "../Services/APIService";
import { useNavigate, useLocation } from "react-router-dom";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import CreatePlanModal from "../Components/CreatePlanModal";
import EditPlanModal from "../Components/EditPlanModal";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";

const PlanTable: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Plan[]>([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [editPlanData, setEditPlanData] = useState<Plan | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const handleLocationState = async () => {
      setLoading(true);
      try {
        const menuID = location.state?.menuID;
        const planDataUpdated = location.state?.planDataUpdated;
        console.log("Location state menu id:", menuID);
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }

        if (planDataUpdated) {
          toast.success("Plan updated successfully");
          fetchPlans();
          const { planDataUpdated: _, ...newState } = location.state;
          navigate(location.pathname, {
            state: newState,
            replace: true,
          });
        }
      } catch (error) {
        console.error("Error handling location state:", error);
      } finally {
        setLoading(false);
      }
    };

    handleLocationState();
  }, [location.state]);

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const subMenuId = location.state?.subMenuId;
        const userDataUpdated = location.state?.userDataUpdated; // Add this

        const previousMenuID = localStorage.getItem("activeMenuID");
        console.log("MenuID from location state:", menuID);
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, [location.state]);

  const fetchPlans = async (): Promise<void> => {
    try {
      setLoading(true);
      const planData = await GetAllPlans();
      console.log("Fetched Plans:", planData);
      setPlans(planData);
      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setLoading(false);
    }
  };

  const handleEdit = (plan: Plan) => {
    setEditPlanData(plan);
    setIsEditModalOpen(true);
  };

  const handleRowSelected = (state: { selectedRows: Plan[] }) => {
    setSelectedRows(state.selectedRows);
  };

  const handleCreateSubmit = () => {
    setIsCreateModalOpen(false);
    setTimeout(() => {
      navigate("/PlanTable", { state: { planDataUpdated: true } });
    }, 1000);
  };

  const handleEditSubmit = () => {
    setIsEditModalOpen(false);
    setEditPlanData(null);
    fetchPlans();
  };

  const filteredPlans = plans.filter(
    (plan) =>
      (plan.description ?? "")
        .toLowerCase()
        .includes(searchText.toLowerCase()) ||
      (plan.amount?.toString() ?? "")
        .toLowerCase()
        .includes(searchText.toLowerCase())
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const columns = [
    {
      name: "Description",
      selector: (row: Plan) => row.description,
      sortable: true,
      width: "200px",
    },
    {
      name: "Amount",
      selector: (row: Plan) => row.amount,
      sortable: true,
      cell: (row: Plan) => formatCurrency(row.amount),
      width: "150px",
    },
    {
      name: "Users",
      selector: (row: Plan) => row.allowedNoOfUser,
      sortable: true,
      width: "100px",
    },
    {
      name: "Leads",
      selector: (row: Plan) => row.allowedNoOfLeads,
      sortable: true,
      width: "100px",
    },
    {
      name: "SMS",
      selector: (row: Plan) => row.allowedSms,
      sortable: true,
      width: "100px",
    },
    {
      name: "Email",
      selector: (row: Plan) => row.allowedNoOfEmail,
      sortable: true,
      width: "100px",
    },
    {
      name: "Validity (Days)",
      selector: (row: Plan) => row.validity,
      sortable: true,
      width: "140px",
    },
    {
      name: "Created On",
      selector: (row: Plan) => row.createdOn,
      sortable: true,
      cell: (row: Plan) => formatDate(row.createdOn),
      width: "130px",
    },
    {
      name: "",
      cell: (row: Plan) => (
        <div className="action-cell">
          <button
            className="action-button edit-button"
            title="Edit Plan"
            onClick={() => handleEdit(row)}
          >
            <i className="bi bi-pencil-square"></i>
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "60px",
    },
  ];

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="wrapper">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12 col-lg-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div className="card-title mb-0">Plan List</div>
                      <div>
                        <input
                          type="text"
                          placeholder="Search by Description or Amount"
                          className="p-2 w-full datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "21rem" }}
                        />
                        <button
                          className="demo-btn btn btn-outline-primary btn-sm"
                          onClick={() => setIsCreateModalOpen(true)}
                        >
                          +Add New Plan
                        </button>
                      </div>
                    </div>
                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredPlans}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        selectableRows
                        selectableRowsComponentProps={{
                          className: "custom-checkbox",
                        }}
                        onSelectedRowsChange={handleRowSelected}
                        clearSelectedRows={toggleCleared}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <CreatePlanModal
              isOpen={isCreateModalOpen}
              onClose={() => setIsCreateModalOpen(false)}
              onSubmit={handleCreateSubmit}
            />
            <EditPlanModal
              isOpen={isEditModalOpen}
              onClose={() => {
                setIsEditModalOpen(false);
                setEditPlanData(null);
              }}
              onSubmit={handleEditSubmit}
              planData={editPlanData}
            />
          </main>
        </div>
        <Footer />
      </div>
      <OverlayLoader
        loading={loading}
        message="Loading Plan Data"
        submessage="This might take a moment..."
      />
    </div>
  );
};

export default PlanTable;
